import { Box } from '@mui/material';

import { OfferSubtype, OfferType } from '@occc-app/offerValidationService/types';
import { getUserTimezone } from '@occc-app/lib/date';
import { useConfig } from '@occc/config';

import { earningMechanismHelper } from '@occc-app/offerHelpers/utils/earningMechanismHelper';
import { getSpinWheelGameInitialValues } from '@occc-app/routes/Game/GameConfigureForm/utils/configureFormInitialValues';
import { getOfferComponentSubType, getOfferComponentType } from '@occc-app/offerHelpers/utils';
import { getScheduleVisibility } from '@occc-app/offerHelpers/ScheduleForm/utils';
import {
  getConstraintBlocksVisibility,
  filterConstraintBlocksVisibility,
} from './sections/constraints-section/constraints-section.utils';
import {
  CONSTRAINTS_SECTION_BLOCKS,
  TARGETING_SECTION_BLOCKS,
} from './sections/constraints-section/constraints-section.constants';
import {
  MarketingContentSection,
  ConfigurationSection,
  TargetingSection,
  ConstraintsSection,
  LimitsSection,
  EngagementSection,
  ScheduleSection,
  LinksSection,
  FulfilmentsSection,
  GameSection,
} from './sections';
import { Offer } from './component-details.utils';
import {
  ComponentDetailsSectionVisibility,
  ConstraintSupplementalData,
} from './component-details.types';
import { DEFAULT_COMPONENT_DETAILS_SECTION_VISIBILITY } from './component-details.constants';
import { getOfferLinks } from './sections/links-section/links-section.utils';

type ComponentDetailsProps = {
  offer: Offer;
  visibleSections?: ComponentDetailsSectionVisibility;
  constraintSupplementalData?: ConstraintSupplementalData;
};

export const ComponentDetails = ({
  offer,
  visibleSections,
  constraintSupplementalData,
}: ComponentDetailsProps) => {
  const { config } = useConfig();
  const type = getOfferComponentType(offer);
  const subType = getOfferComponentSubType(offer);
  const isFuelRollbackOffer =
    type === OfferType.FuelRollback || subType === OfferSubtype.FuelRollback;
  const sections = visibleSections || DEFAULT_COMPONENT_DETAILS_SECTION_VISIBILITY;

  const userTimezone = getUserTimezone();
  const marketingContent = offer.marketingContents?.[0];

  const displayCategories =
    offer.displayCategories?.map((dc) => ({
      name: dc.name,
      id: dc.id,
    })) || [];

  // This is a temporary solution in which the rank is used inside the customData.
  // This will be replaced in the future by a stronger approach. @Dasha Gakh
  let displayRank: number | undefined = undefined;
  if (offer.customData && 'rank' in offer.customData) {
    const parsedRank = Number(offer.customData.rank);
    displayRank = Number.isFinite(parsedRank) ? parsedRank : undefined;
  }

  let spinWheelGameValues = null;
  if (offer.earningMechanism && earningMechanismHelper.isSpinWheelGame(offer.earningMechanism)) {
    spinWheelGameValues = getSpinWheelGameInitialValues(offer);
  }

  const offerLinks = getOfferLinks({
    config,
    offerId: offer.id,
    requiresActivation: !!offer.preparedConstraints.requiresActivation,
    subBrandUuids: offer.preparedConstraints.subBrand?.subBrandUuids,
  });

  const constraintBlocksVisibility = getConstraintBlocksVisibility(offer);

  return (
    <Box display='flex' flexDirection='column' gap={1} mt={{ xs: 2, md: 3 }}>
      {sections.marketing && (
        <MarketingContentSection
          marketingContent={marketingContent}
          presentationType={offer.presentationType}
          presentationPriority={offer.presentationPriority}
          displayCategories={displayCategories}
          displayRank={displayRank}
          category={offer.category}
          fundingSource={offer.fundingSource}
          fundingSourceName={offer.fundingSourceName}
          displayForOrderAhead={offer.displayForOrderAhead}
          trackingConfig={offer.trackingConfig}
        />
      )}

      {sections.configuration && (
        <ConfigurationSection
          earningMechanism={offer.earningMechanism}
          engagementType={offer.engagementType}
          offerType={type}
          offerSubType={subType}
          shortPromptAtThePump={offer.shortDescription}
          longPromptAtThePump={offer.longDescription}
          visibleForCommandCenterFulfillment={offer.visibleForCommandCenterFulfillment ?? false}
          customData={offer.customData}
          scanDataConfiguration={offer.scanDataConfiguration}
        />
      )}

      {sections.game && spinWheelGameValues && (
        <GameSection
          gameType={spinWheelGameValues.gameType}
          gameConfig={spinWheelGameValues.spinWheelGameConfig}
          segments={spinWheelGameValues.segments}
          isGameplayAmountDisplayed={type === OfferType.VoucheredGame}
        />
      )}

      {sections.targeting && (
        <TargetingSection
          offerType={type}
          constraints={offer.constraints}
          visibleBlocks={filterConstraintBlocksVisibility(
            TARGETING_SECTION_BLOCKS,
            constraintBlocksVisibility,
          )}
        />
      )}

      {sections.constraints && (
        <ConstraintsSection
          constraints={offer.constraints}
          visibleBlocks={filterConstraintBlocksVisibility(
            CONSTRAINTS_SECTION_BLOCKS,
            constraintBlocksVisibility,
          )}
          constraintSupplementalData={constraintSupplementalData}
          isStackable={subType === 'FuelRollback' ? offer.stackable : undefined}
        />
      )}

      {sections.limits && <LimitsSection limits={offer.limits} />}

      {sections.engagement && (
        <EngagementSection
          availableMessage={marketingContent?.triggeredMessageContent?.visibilitySms}
          fulfillmentMessage={marketingContent?.triggeredMessageContent?.fulfillmentSms}
        />
      )}

      {sections.schedule && (
        <ScheduleSection
          publishAt={offer.publishAt ?? null}
          expireAt={offer.expireAt ?? null}
          timezone={userTimezone}
          isTriggeredOnVisibility={offer?.triggerPreferences.triggerOnVisibility}
          isTriggeredOnFulfillment={offer?.triggerPreferences.triggerOnFulfillment}
          isTriggeredOnProgress={offer?.triggerPreferences.triggerOnProgress}
          triggeredOnVisibilityMessage={marketingContent?.triggeredMessageContent.visibilitySms}
          triggeredOnProgressMessage={marketingContent?.triggeredMessageContent.progressSms}
          triggeredOnFulfillmentMessage={marketingContent?.triggeredMessageContent.fulfillmentSms}
          visibleBlocks={getScheduleVisibility(offer, type)}
        />
      )}

      {sections.links && type !== OfferType.StoredValue && <LinksSection links={offerLinks} />}

      {sections.fulfilment && (
        <FulfilmentsSection
          fulfillmentCount={offer.fulfillmentCount}
          earningMechanism={offer.earningMechanism}
          isFuelRollbackOffer={isFuelRollbackOffer}
        />
      )}
    </Box>
  );
};
